import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Trash2, Download } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { toast } from 'react-toastify';
import {
  getPublicIdFromCloudinaryUrl,
  isCloudinaryUrl,
} from '../../../../utils/cloudinaryUpload';
import { uploadToCdn, deleteFromCdn, isUnicornCdnUrl } from '../../../../utils/cdnUpload';

interface DocumentsSectionProps {
  propertyId?: string;
  isEditing: boolean;
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({ propertyId, isEditing }) => {
  const [document, setDocument] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (propertyId) {
      fetchDocument();
    } else {
      setLoading(false);
    }
  }, [propertyId]);

  const fetchDocument = async () => {
    try {
      const { data, error } = await supabase
        .from('property_documents')
        .select('*')
        .eq('property_id', propertyId)
        .eq('type', 'planimetria')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setDocument(data);
    } catch (error) {
      console.error('Error fetching document:', error);
      toast.error('Errore nel caricamento della planimetria');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (!propertyId) {
      toast.error('Salva prima l\'immobile per caricare la planimetria');
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload to Unicorn CDN via Netlify proxy (API key never touches the browser).
      // Unicorn auto-routes PDFs to /files/ and images to /img/ based on MIME.
      const result = await uploadToCdn(
        file,
        (percent) => setUploadProgress(percent)
      );

      const publicUrl = result.cdn_url;
      const unicornFileId = result.file?.id ?? null;

      // Upsert DB record
      const { error: dbError } = await supabase
        .from('property_documents')
        .upsert({
          property_id: propertyId,
          type: 'planimetria',
          url: publicUrl,
          unicorn_file_id: unicornFileId,
        });

      if (dbError) throw dbError;

      toast.success('Planimetria caricata con successo');
      fetchDocument();
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast.error(`Errore nel caricamento: ${error.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 10485760, // 10MB
    multiple: false,
    disabled: !isEditing || !propertyId || uploading,
  });

  const handleRemoveDocument = async () => {
    if (!document) return;

    try {
      // Route deletion by origin (Unicorn / Cloudinary legacy / Supabase storage).
      // All best-effort: the DB record is the source of truth, a CDN orphan is not user-visible.
      if (document.url && isUnicornCdnUrl(document.url) && document.unicorn_file_id) {
        deleteFromCdn(document.unicorn_file_id)
          .catch(err => console.warn('Unicorn delete warning:', err));
      } else if (document.url && isCloudinaryUrl(document.url)) {
        const publicId = getPublicIdFromCloudinaryUrl(document.url);
        if (publicId) {
          fetch('/.netlify/functions/cloudinary-delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ publicId, resourceType: 'raw' }),
          }).catch(err => console.warn('Cloudinary delete warning:', err));
        }
      } else if (document.url) {
        const filePath = document.url.split('/').pop();
        if (filePath) {
          await supabase.storage
            .from('documents')
            .remove([`${propertyId}/${filePath}`])
            .catch(err => console.warn('Supabase storage delete warning:', err));
        }
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('property_documents')
        .delete()
        .eq('id', document.id);

      if (dbError) throw dbError;

      toast.success('Planimetria eliminata con successo');
      setDocument(null);
    } catch (error: any) {
      console.error('Error removing document:', error);
      toast.error(`Errore: ${error.message}`);
    }
  };

  const isPdf = (url: string) =>
    url?.toLowerCase().includes('.pdf') || url?.toLowerCase().includes('/raw/');

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center">
          <FileText className="h-5 w-5 mr-2 text-primary" />
          Planimetria
        </h3>
        <div className="flex justify-center py-8">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center">
        <FileText className="h-5 w-5 mr-2 text-primary" />
        Planimetria
      </h3>

      <div>
        {!document ? (
          <div
            {...getRootProps()}
            className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 ${
              !isEditing || !propertyId || uploading
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                : isDragActive
                  ? 'border-primary border-dashed bg-primary/5'
                  : 'border-gray-300 border-dashed hover:border-primary transition-colors'
            } rounded-md`}
          >
            <div className="space-y-1 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label
                  className={`relative ${
                    !isEditing || !propertyId || uploading
                      ? 'cursor-not-allowed text-gray-400'
                      : 'cursor-pointer text-primary hover:text-primary-light'
                  }`}
                >
                  <span>Carica planimetria</span>
                  <input {...getInputProps()} />
                </label>
                <p className="pl-1">o trascina qui</p>
              </div>
              <p className="text-xs text-gray-500">PDF, JPG, PNG fino a 10MB</p>
              {!isEditing && (
                <p className="text-xs text-red-500 mt-2">
                  Salva l'immobile prima di caricare la planimetria
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Planimetria</p>
                <p className="text-sm text-gray-500">
                  Caricato il {new Date(document.created_at).toLocaleDateString('it-IT')}
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <a
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                download={isPdf(document.url) ? 'planimetria.pdf' : undefined}
                className="p-2 text-blue-600 hover:text-blue-800 transition-colors rounded-md hover:bg-blue-50"
                title={isPdf(document.url) ? 'Scarica PDF' : 'Visualizza'}
              >
                {isPdf(document.url) ? (
                  <Download className="h-5 w-5" />
                ) : (
                  <FileText className="h-5 w-5" />
                )}
              </a>
              {isEditing && (
                <button
                  onClick={handleRemoveDocument}
                  className="p-2 text-red-600 hover:text-red-700 transition-colors rounded-md hover:bg-red-50"
                  title="Elimina"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-primary h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 text-center mt-2">
              Caricamento in corso... {uploadProgress}%
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsSection;
